import { useParams, Link } from 'react-router-dom';
import SEOHead from '@/components/feature/SEOHead';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { getArticleBySlug, articles } from '@/mocks/articles';

const articleDetailImages: Record<string, string> = {
  'article-bt-guide-2025': 'https://readdy.ai/api/search-image?query=Modern%20digital%20search%20technology%20abstract%20with%20warm%20golden%20amber%20gradient%2C%20network%20nodes%20and%20data%20flow%20visualization%2C%20clean%20minimalist%20editorial%20style%2C%20cinematic%20lighting%2C%20no%20text%20no%20human%20faces&width=1400&height=600&seq=detail-bt-guide&orientation=landscape',
  'article-magnet-vs-torrent': 'https://readdy.ai/api/search-image?query=Abstract%20magnetic%20connection%20visualization%20with%20warm%20terracotta%20and%20amber%20gradient%2C%20digital%20network%20topology%20concept%2C%20modern%20minimalist%20style%2C%20cinematic%20depth%2C%20no%20text%20no%20human%20faces&width=1400&height=600&seq=detail-magnet&orientation=landscape',
  'article-summer-movies': 'https://readdy.ai/api/search-image?query=Cinematic%20film%20reel%20and%20golden%20projector%20light%20abstract%20with%20warm%20bokeh%20effects%2C%20movie%20entertainment%20concept%2C%20elegant%20artistic%20composition%2C%20no%20text%20no%20human%20faces&width=1400&height=600&seq=detail-movies&orientation=landscape',
  'article-lossless-music': 'https://readdy.ai/api/search-image?query=Elegant%20sound%20wave%20frequency%20spectrum%20abstract%20with%20warm%20golden%20gradient%2C%20audio%20visualization%20concept%2C%20modern%20minimalist%20design%2C%20soft%20cinematic%20lighting%2C%20no%20text%20no%20human%20faces&width=1400&height=600&seq=detail-music&orientation=landscape',
  'article-open-source': 'https://readdy.ai/api/search-image?query=Modern%20code%20editor%20and%20software%20development%20abstract%20with%20warm%20amber%20gradient%2C%20clean%20geometric%20patterns%2C%20minimalist%20tech%20concept%2C%20no%20text%20no%20human%20faces&width=1400&height=600&seq=detail-opensource&orientation=landscape',
  'article-indie-games': 'https://readdy.ai/api/search-image?query=Abstract%20gaming%20controller%20and%20pixel%20art%20elements%20with%20warm%20golden%20gradient%2C%20modern%20playful%20tech%20vibe%2C%20geometric%20composition%2C%20no%20text%20no%20human%20faces&width=1400&height=600&seq=detail-games&orientation=landscape',
  'article-ebook-recommend': 'https://readdy.ai/api/search-image?query=Abstract%20open%20book%20with%20digital%20glowing%20pages%20and%20warm%20amber%20gradient%2C%20knowledge%20and%20learning%20concept%2C%20modern%20minimalist%2C%20no%20text%20no%20human%20faces&width=1400&height=600&seq=detail-ebook&orientation=landscape',
  'article-safety-guide': 'https://readdy.ai/api/search-image?query=Abstract%20digital%20shield%20and%20security%20lock%20with%20warm%20terracotta%20gradient%2C%20modern%20cybersecurity%20concept%2C%20clean%20geometric%20composition%2C%20no%20text%20no%20human%20faces&width=1400&height=600&seq=detail-safety&orientation=landscape',
};

const defaultDetailImage = 'https://readdy.ai/api/search-image?query=Modern%20digital%20technology%20abstract%20with%20warm%20golden%20amber%20gradient%2C%20clean%20minimalist%20editorial%20style%2C%20cinematic%20lighting%2C%20no%20text%20no%20human%20faces&width=1400&height=600&seq=detail-default&orientation=landscape';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return (
      <>
        <SEOHead title="文章未找到 - 种子搜索" description="你查找的文章不存在或已被移除。" />
        <Navbar />
        <main className="pt-16 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-file-search-line text-3xl text-foreground-400"></i>
            </div>
            <h1 className="text-xl font-bold font-heading text-foreground-900 mb-2">文章未找到</h1>
            <p className="text-sm text-foreground-500 mb-6">你查找的文章不存在或已被移除</p>
            <Link
              to="/"
              className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-medium bg-primary-500 text-white rounded-lg hover:bg-primary-600 transition-colors"
            >
              <i className="ri-arrow-left-line"></i>
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const pageSchema = {
    '@context': 'https://seeddex.github.io',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    author: { '@type': 'Person', name: article.author },
    datePublished: article.date,
    url: `https://seeddex.github.io/article/${article.slug}`,
    inLanguage: 'zh-CN',
    articleSection: article.categoryName,
    keywords: article.tags.join(','),
  };

  const breadcrumbSchema = {
    '@context': 'https://seeddex.github.io',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: '首页', item: 'https://seeddex.github.io/' },
      { '@type': 'ListItem', position: 2, name: article.categoryName, item: `https://seeddex.github.io/category/${article.category}` },
      { '@type': 'ListItem', position: 3, name: article.title },
    ],
  };

  const relatedArticles = articles
    .filter((a) => a.slug !== article.slug && a.category === article.category)
    .slice(0, 3);

  return (
    <>
      <SEOHead
        title={`${article.title} - 种子搜索`}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://seeddex.github.io/article/${article.slug}`}
        ogType="article"
        schemaJson={{ ...pageSchema, ...breadcrumbSchema }}
      />
      <ScrollToTop />
      <Navbar />

      <main className="pt-16">
        <article>
          <div className="relative w-full h-[300px] md:h-[400px] overflow-hidden">
            <img
              src={articleDetailImages[article.imageSeq] || defaultDetailImage}
              alt={article.title}
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent"></div>
            <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 max-w-[900px] mx-auto">
              <span className="inline-block px-2.5 py-1 text-xs font-medium rounded-md bg-primary-500 text-white mb-3">
                {article.categoryName}
              </span>
              <h1 className="text-2xl md:text-3xl font-bold font-heading text-white leading-tight">
                {article.title}
              </h1>
            </div>
          </div>

          <div className="max-w-[800px] mx-auto px-4 md:px-6 py-6">
            <Breadcrumb
              items={[
                { label: '首页', path: '/' },
                { label: article.categoryName, path: `/category/${article.category}` },
                { label: article.title },
              ]}
            />

            <div className="flex items-center gap-4 mb-8 text-sm text-foreground-500">
              <div className="flex items-center gap-1.5">
                <i className="ri-user-line"></i>
                <span>{article.author}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i>
                <span>{article.date}</span>
              </div>
              <div className="flex items-center gap-1.5">
                <i className="ri-time-line"></i>
                <span>{article.readTime}阅读</span>
              </div>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed
                prose-h2:text-xl prose-h2:font-bold prose-h2:text-foreground-900 prose-h2:mt-8 prose-h2:mb-4
                prose-p:text-sm md:prose-p:text-base prose-p:leading-relaxed prose-p:mb-4
                prose-strong:text-foreground-900"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <h3 className="text-sm font-medium text-foreground-500 mb-3">标签</h3>
              <div className="flex flex-wrap gap-2">
                {article.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 text-xs rounded-full bg-secondary-100 text-secondary-700"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-12 md:py-16 bg-background-100">
            <div className="max-w-[800px] mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-900 mb-6">
                相关文章
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {relatedArticles.map((rel) => (
                  <Link
                    key={rel.slug}
                    to={`/article/${rel.slug}`}
                    className="group bg-background-50 rounded-lg p-4 border border-background-200/70 hover:border-primary-200/60 transition-all"
                  >
                    <h3 className="text-sm font-semibold font-heading text-foreground-900 mb-1.5 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {rel.title}
                    </h3>
                    <p className="text-xs text-foreground-500 line-clamp-2">{rel.excerpt}</p>
                  </Link>
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}